import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOktaAuth } from '@okta/okta-react';
import { GithubIcon, GoogleSigninIcon, SsoIcon } from '@redislabsdev/redis-ui-icons/multicolor';
import { useCallback, useEffect, useRef } from 'react';
import * as S from './SocialLogin.style';
import SocialLoginButton from './SocialLoginButton/SocialLoginButton';
import { routes } from '../../../../utils/constants/routes';
import { i18translation } from '../../../../locale/translations/i18NsPaths';
import { handleSocialLogin, setLoginType } from '../../utils/LoginHelper';
import { parametersQueries } from '../../../../queryClient/parameters/parameters.queries';
import { useLoginContext } from '../../Login.context';
import { useFeatureFlags } from '../../../../components/FeatureFlagsProvider/hooks/useFeatureFlags';
import { closeOktaSession } from '../../../../utils/oktaInstance';

const SocialSignIn = ({ allowSSo }: { allowSSo?: boolean }) => {
  const { search } = useLocation();
  const history = useHistory();
  const { systemParams } = parametersQueries.useSystemParameters();
  const { oktaAuth } = useOktaAuth();
  const { t } = useTranslation();
  const keyPrefix = i18translation.LoginScreen;
  const showLoaderContext = useLoginContext();
  const searchParams = new URLSearchParams(search);
  const searchParamsAsObject = Object.fromEntries(searchParams);
  const {
    flags: { enableKeepOktaSession }
  } = useFeatureFlags();

  const isRedirectingRef = useRef(false);
  const thirdPartyItems = useCallback(
    (service: string) => {
      showLoaderContext.setShowLoader(true);

      if (search) {
        window.sessionStorage.setItem('queryParams', search);
      }

      // used to save query params for when the request is finished and pass them to the login
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      handleSocialLogin(
        service === 'google' ? systemParams.okta_google_idp_id : systemParams.okta_github_idp_id,
        oktaAuth,
        service, // used to display the appropriate text inside the migration window
        enableKeepOktaSession
      );
    },
    [showLoaderContext, search, systemParams, oktaAuth, enableKeepOktaSession]
  );

  // Redis University uses loginMethod query param to redirect students after login
  useEffect(() => {
    if (isRedirectingRef.current) {
      return;
    }

    switch (searchParamsAsObject?.loginMethod) {
      case 'google':
      case 'github':
        isRedirectingRef.current = true;
        thirdPartyItems(searchParamsAsObject.loginMethod);
        break;
      case 'saml':
        isRedirectingRef.current = true;
        window.sessionStorage.setItem('queryParams', search);
        history.push(routes.saml);
        break;
      default:
        break;
    }
  }, [searchParamsAsObject?.loginMethod, thirdPartyItems, search, history]);

  // Handle socialMethod query param
  useEffect(() => {
    if (isRedirectingRef.current) {
      return;
    }

    if (searchParamsAsObject?.socialMethod) {
      isRedirectingRef.current = true;
      thirdPartyItems(searchParamsAsObject.socialMethod);
    }
  }, [searchParamsAsObject?.socialMethod, thirdPartyItems]);

  useEffect(() => {
    if (isRedirectingRef.current) {
      return;
    }

    const idpId = searchParamsAsObject?.idpId;

    if (idpId) {
      isRedirectingRef.current = true;

      const handleIdpRedirect = async () => {
        setLoginType('sso');
        showLoaderContext.setShowLoader(true);

        // Close Okta session before IDP redirect when flag is enabled
        if (enableKeepOktaSession) {
          await closeOktaSession();
        }

        await oktaAuth.signInWithRedirect({ idp: idpId });
      };

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      handleIdpRedirect();
    }
  }, [searchParamsAsObject?.idpId, showLoaderContext, enableKeepOktaSession, oktaAuth]);

  return (
    <S.SocialLoginContainer>
      <SocialLoginButton
        dataTestId="button--sign-in-via-google-account"
        onClick={() => thirdPartyItems('google')}
        name={t(keyPrefix.google)}
        icon={<GoogleSigninIcon customSize="4rem" />}
      />
      <SocialLoginButton
        dataTestId="button--sign-in-via-github-account"
        onClick={() => thirdPartyItems('github')}
        name={t(keyPrefix.github)}
        icon={<GithubIcon customSize="4rem" />}
      />
      {allowSSo && (
        <SocialLoginButton
          dataTestId="button--sign-in-via-saml"
          onClick={() => history.push(routes.saml)}
          name={t(keyPrefix.ssoBtn)}
          icon={<SsoIcon customSize="4rem" />}
        />
      )}
    </S.SocialLoginContainer>
  );
};

export default SocialSignIn;
